import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faFilter } from '@fortawesome/free-solid-svg-icons';

interface FirmwareUpdatesChartProps {
  apiBaseUrl: string;
}

interface SeriesMeta {
  key: string;
  label: string;
  count: number;
}

interface FacetOption {
  value: string;
  label: string;
  count: number;
}

interface MetricsResponse {
  groupBy: string;
  bucket: string;
  history: Array<Record<string, string | number>>;
  series: SeriesMeta[];
  facets: {
    methods: FacetOption[];
    fromFirmware: FacetOption[];
    toFirmware: FacetOption[];
    statuses: FacetOption[];
    models: FacetOption[];
    deploymentTypes: FacetOption[];
  };
  totals: {
    total: number;
    success: number;
    failure: number;
    in_progress: number;
  };
}

type TimeRange = { value: number; label: string };
type ChartKind = 'stackedArea' | 'stackedBar';
type GroupBy = 'method' | 'fromFirmware' | 'toFirmware' | 'status' | 'model' | 'deploymentType';
type Bucket = 'minute' | 'hour' | 'day' | 'week' | 'month';

const COLORS = [
  '#2563eb', '#db2777', '#059669', '#ea580c', '#7c3aed',
  '#0891b2', '#dc2626', '#65a30d', '#4f46e5', '#AAAAAA',
];

const TIME_RANGES: TimeRange[] = [
  { value: 7, label: '1 Week' },
  { value: 30, label: '1 Month' },
  { value: 90, label: '3 Months' },
  { value: 180, label: '6 Months' },
  { value: 365, label: '1 Year' },
  { value: 0, label: 'All Time' },
];

const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: 'method', label: 'Type' },
  { value: 'fromFirmware', label: 'From version' },
  { value: 'toFirmware', label: 'To version' },
  { value: 'status', label: 'Status' },
  { value: 'model', label: 'Model' },
  { value: 'deploymentType', label: 'Deployment' },
];

const GRANULARITY: { value: Bucket; label: string }[] = [
  { value: 'minute', label: 'By minute' },
  { value: 'hour', label: 'By hour' },
  { value: 'day', label: 'By day' },
  { value: 'week', label: 'By week' },
  { value: 'month', label: 'By month' },
];

const defaultBucket = (days: number): Bucket => {
  if (days > 0 && days <= 7) return 'hour';
  if (days > 0 && days <= 90) return 'day';
  if (days > 0 && days <= 365) return 'week';
  return 'month';
};

const formatVersion = (value: string) => {
  if (!value) return '—';
  return /^[vV]/.test(value) ? value : `v${value}`;
};

const statusBadge = (status: string) => {
  if (status === 'success') return 'bg-success';
  if (status === 'failure') return 'bg-danger';
  if (status === 'in_progress') return 'bg-warning text-dark';
  return 'bg-secondary';
};

const formatDate = (value: string | number, bucket: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  if (bucket === 'minute') {
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }
  if (bucket === 'hour') {
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' });
  }
  if (bucket === 'month') {
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const FilterDropdown: React.FC<{
  label: string;
  options: FacetOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const summary = selected.length === 0 ? 'All' : `${selected.length} selected`;

  return (
    <div className="dropdown" ref={ref}>
      <button
        type="button"
        className={`btn btn-sm ${selected.length ? 'btn-primary' : 'btn-outline-secondary'}`}
        onClick={() => setOpen((value) => !value)}
      >
        <FontAwesomeIcon icon={faFilter} className="me-1" />
        {label}: {summary}
        <FontAwesomeIcon icon={faChevronDown} className="ms-1" />
      </button>
      {open && (
        <div
          className="dropdown-menu show p-2"
          style={{ minWidth: 220, maxHeight: 280, overflowY: 'auto' }}
        >
          <button
            type="button"
            className="dropdown-item d-flex justify-content-between align-items-center"
            onClick={() => onChange([])}
          >
            All
            {selected.length === 0 && <FontAwesomeIcon icon={faCheck} />}
          </button>
          <div className="dropdown-divider" />
          {options.length === 0 ? (
            <div className="dropdown-item-text text-muted small">No values yet</div>
          ) : options.map((option) => {
            const checked = selected.includes(option.value);
            return (
              <button
                type="button"
                key={option.value}
                className="dropdown-item d-flex justify-content-between align-items-center"
                onClick={() => {
                  onChange(
                    checked
                      ? selected.filter((value) => value !== option.value)
                      : [...selected, option.value]
                  );
                }}
              >
                <span>
                  {checked && <FontAwesomeIcon icon={faCheck} className="me-2" />}
                  {option.label}
                </span>
                <span className="text-muted small ms-2">{option.count.toLocaleString()}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface DeviceListUpdate {
  id: string;
  serial: string;
  model: string;
  fromFirmware: string;
  toFirmware: string;
  method: string;
  methodLabel: string;
  status: string;
  statusLabel: string;
  deploymentType: string;
  deploymentLabel: string;
  startedAt: string;
  finishedAt: string | null;
  source: string;
  failureReason: string;
  user: { id: string; email: string; name: string } | null;
}

interface DeviceListResponse {
  date: string;
  bucket: string;
  groupBy: string;
  series: string;
  label: string;
  total: number;
  uniqueDevices: number;
  truncated: boolean;
  updates: DeviceListUpdate[];
}

interface DeviceListSelection {
  date: string | number;
  label: string;
  seriesKey: string;
  seriesLabel: string;
}

interface HoverPoint {
  label: string;
  date: string | number;
  rows: Array<{ key: string; name: string; value: number; color?: string }>;
}

const pointFromChartState = (state: {
  activeLabel?: string | number;
  activePayload?: Array<{
    dataKey?: string | number;
    name?: string | number;
    value?: unknown;
    color?: string;
    payload?: { date?: string | number };
  }>;
} | null): HoverPoint | null => {
  const payload = state?.activePayload;
  if (!payload?.length) return null;
  const rows = payload
    .filter((item) => Number(item.value) > 0)
    .map((item) => ({
      key: String(item.dataKey),
      name: String(item.name || item.dataKey),
      value: Number(item.value),
      color: item.color,
    }));
  const date = payload[0]?.payload?.date;
  if (!rows.length || date == null) return null;
  return {
    label: String(state?.activeLabel || ''),
    date,
    rows,
  };
};

const tooltipBoxStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #ccc',
  padding: '10px',
  whiteSpace: 'nowrap',
};

const FirmwareUpdatesTooltipBody: React.FC<{
  point: HoverPoint;
  onSeeDevices?: (selection: DeviceListSelection) => void;
}> = ({ point, onSeeDevices }) => (
  <div style={tooltipBoxStyle}>
    <div className="mb-1">{point.label}</div>
    {point.rows.map((row) => (
      <div key={row.key} className="mb-1">
        <div style={{ color: row.color }}>
          {row.name} : {row.value.toLocaleString()}
        </div>
        {onSeeDevices && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={() => {
              onSeeDevices({
                date: point.date,
                label: point.label,
                seriesKey: row.key,
                seriesLabel: row.name,
              });
            }}
          >
            See Device List
          </button>
        )}
      </div>
    ))}
  </div>
);

const FirmwareUpdateDevicesModal: React.FC<{
  selection: DeviceListSelection;
  data: DeviceListResponse | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}> = ({ selection, data, loading, error, onClose }) => {
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.body.classList.add('modal-open');
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('modal-open');
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const title = `${selection.seriesLabel} · ${selection.label}`;

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title mb-0">{title}</h5>
                {data && (
                  <div className="text-muted small">
                    {data.total.toLocaleString()} update{data.total === 1 ? '' : 's'}
                    {data.uniqueDevices !== data.total
                      ? ` · ${data.uniqueDevices.toLocaleString()} device${data.uniqueDevices === 1 ? '' : 's'}`
                      : ''}
                    {data.truncated ? ' · showing first 500' : ''}
                  </div>
                )}
              </div>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
            </div>
            <div className="modal-body">
              {loading ? (
                <div className="text-center my-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="alert alert-danger mb-0">{error}</div>
              ) : !data?.updates.length ? (
                <div className="alert alert-info mb-0">No devices in this collection.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Serial</th>
                        <th>Model</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Status</th>
                        <th>Type</th>
                        <th>Deployment</th>
                        <th>Owner</th>
                        <th>Started</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.updates.map((update) => (
                        <tr key={update.id}>
                          <td>
                            <Link
                              to={`/device/${encodeURIComponent(update.serial)}?returnTo=${encodeURIComponent(returnTo)}`}
                            >
                              {update.serial}
                            </Link>
                          </td>
                          <td>{update.model || '—'}</td>
                          <td>{formatVersion(update.fromFirmware)}</td>
                          <td>{formatVersion(update.toFirmware)}</td>
                          <td>
                            <span className={`badge ${statusBadge(update.status)}`}>
                              {update.statusLabel}
                              {update.failureReason ? ` · ${update.failureReason}` : ''}
                            </span>
                          </td>
                          <td>{update.methodLabel}</td>
                          <td>{update.deploymentLabel}</td>
                          <td>{update.user?.name || update.user?.email || '—'}</td>
                          <td className="text-nowrap">
                            {update.startedAt
                              ? new Date(update.startedAt).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const FirmwareUpdatesChart: React.FC<FirmwareUpdatesChartProps> = ({ apiBaseUrl }) => {
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRange, setSelectedRange] = useState<TimeRange>(TIME_RANGES[1]);
  const [groupBy, setGroupBy] = useState<GroupBy>('method');
  const [chartKind, setChartKind] = useState<ChartKind>('stackedArea');
  const [methods, setMethods] = useState<string[]>([]);
  const [fromFirmware, setFromFirmware] = useState<string[]>([]);
  const [toFirmware, setToFirmware] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [deploymentTypes, setDeploymentTypes] = useState<string[]>([]);
  const [bucket, setBucket] = useState<Bucket>(defaultBucket(TIME_RANGES[1].value));
  const [deviceList, setDeviceList] = useState<DeviceListSelection | null>(null);
  const [deviceListData, setDeviceListData] = useState<DeviceListResponse | null>(null);
  const [deviceListLoading, setDeviceListLoading] = useState(false);
  const [deviceListError, setDeviceListError] = useState<string | null>(null);
  const [pinnedTooltip, setPinnedTooltip] = useState<{
    point: HoverPoint;
    left: number;
    top: number;
  } | null>(null);

  const filterParams = useMemo(() => ({
    days: selectedRange.value,
    groupBy,
    bucket,
    method: methods.join(',') || undefined,
    fromFirmware: fromFirmware.join(',') || undefined,
    toFirmware: toFirmware.join(',') || undefined,
    status: statuses.join(',') || undefined,
    model: models.join(',') || undefined,
    deploymentType: deploymentTypes.join(',') || undefined,
  }), [selectedRange, groupBy, bucket, methods, fromFirmware, toFirmware, statuses, models, deploymentTypes]);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/devices/firmware-update-metrics`, {
        withCredentials: true,
        params: filterParams,
      });
      setMetrics(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching firmware update metrics:', err);
      setError('Failed to load firmware updates');
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, filterParams]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const openDeviceList = useCallback((selection: DeviceListSelection) => {
    setPinnedTooltip(null);
    setDeviceList(selection);
  }, []);

  const pinTooltipFromEvent = useCallback((event: React.MouseEvent<HTMLElement>, point: HoverPoint) => {
    const wrap = event.currentTarget.closest('.recharts-tooltip-wrapper') as HTMLElement | null;
    const rect = (wrap || event.currentTarget).getBoundingClientRect();
    setPinnedTooltip({ point, left: rect.left, top: rect.top });
  }, []);

  const closeDeviceList = useCallback(() => {
    setDeviceList(null);
    setDeviceListData(null);
    setDeviceListError(null);
  }, []);

  useEffect(() => {
    if (!deviceList) return undefined;
    let cancelled = false;
    setDeviceListLoading(true);
    setDeviceListError(null);
    setDeviceListData(null);
    axios.get(`${apiBaseUrl}/devices/firmware-update-devices`, {
      withCredentials: true,
      params: {
        ...filterParams,
        date: deviceList.date,
        series: deviceList.seriesKey,
      },
    }).then((response) => {
      if (!cancelled) setDeviceListData(response.data);
    }).catch((err) => {
      console.error('Error fetching firmware update devices:', err);
      if (!cancelled) setDeviceListError('Failed to load device list');
    }).finally(() => {
      if (!cancelled) setDeviceListLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, deviceList, filterParams]);

  const chartData = useMemo(() => {
    if (!metrics?.history) return [];
    return metrics.history.map((point) => ({
      ...point,
      label: formatDate(String(point.date), metrics.bucket),
      total: Number(point.total) || 0,
    }));
  }, [metrics]);

  const series = metrics?.series || [];
  const filteredTotal = chartData.reduce((sum, point) => sum + point.total, 0);

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div>
          <h5 className="card-title mb-0">Firmware Updates</h5>
          <div className="text-muted small">Updates over time by type, version, and status</div>
        </div>
        <div className="btn-group">
          <button
            type="button"
            className={`btn btn-sm ${chartKind === 'stackedArea' ? 'btn-secondary' : 'btn-outline-secondary'}`}
            onClick={() => setChartKind('stackedArea')}
          >
            Area
          </button>
          <button
            type="button"
            className={`btn btn-sm ${chartKind === 'stackedBar' ? 'btn-secondary' : 'btn-outline-secondary'}`}
            onClick={() => setChartKind('stackedBar')}
          >
            Bars
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div className="d-flex flex-wrap gap-1">
            {TIME_RANGES.map((range) => (
              <button
                key={range.label}
                type="button"
                className={`btn btn-sm ${selectedRange.label === range.label ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => {
                  setSelectedRange(range);
                  setBucket(defaultBucket(range.value));
                }}
              >
                {range.label}
              </button>
            ))}
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="small text-muted">Group by</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={groupBy}
              onChange={(event) => setGroupBy(event.target.value as GroupBy)}
            >
              {GROUP_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
          <span className="small text-muted">Granularity</span>
          <div className="btn-group">
            {GRANULARITY.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`btn btn-sm ${bucket === option.value ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => setBucket(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-3 d-flex flex-wrap gap-2">
          <FilterDropdown
            label="Type"
            options={metrics?.facets.methods || []}
            selected={methods}
            onChange={setMethods}
          />
          <FilterDropdown
            label="From"
            options={metrics?.facets.fromFirmware || []}
            selected={fromFirmware}
            onChange={setFromFirmware}
          />
          <FilterDropdown
            label="To"
            options={metrics?.facets.toFirmware || []}
            selected={toFirmware}
            onChange={setToFirmware}
          />
          <FilterDropdown
            label="Status"
            options={metrics?.facets.statuses || []}
            selected={statuses}
            onChange={setStatuses}
          />
          <FilterDropdown
            label="Model"
            options={metrics?.facets.models || []}
            selected={models}
            onChange={setModels}
          />
          <FilterDropdown
            label="Deployment"
            options={metrics?.facets.deploymentTypes || []}
            selected={deploymentTypes}
            onChange={setDeploymentTypes}
          />
        </div>

        {loading ? (
          <div className="text-center my-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="alert alert-danger mb-0">{error}</div>
        ) : !chartData.length ? (
          <div className="alert alert-info text-center py-5 mb-0">
            <h5>No firmware updates yet</h5>
            <p className="mb-0">
              New OTA, sOTA, recovery, and web-flasher events will show up here as they are recorded.
            </p>
          </div>
        ) : (
          <div style={{ height: 400, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              {chartKind === 'stackedBar' ? (
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 25 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" interval="preserveStartEnd" minTickGap={24} />
                  <YAxis allowDecimals={false} />
                  <Tooltip
                    wrapperStyle={{ pointerEvents: pinnedTooltip ? 'none' : 'auto', outline: 'none' }}
                    content={(props) => {
                      const point = pointFromChartState({
                        activeLabel: props.label,
                        activePayload: props.payload,
                      });
                      if (pinnedTooltip || !props.active || !point) return null;
                      return (
                        <div onMouseEnter={(event) => pinTooltipFromEvent(event, point)}>
                          <FirmwareUpdatesTooltipBody point={point} onSeeDevices={openDeviceList} />
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  {series.map((item, index) => (
                    <Bar
                      key={item.key}
                      dataKey={item.key}
                      name={item.label}
                      stackId="updates"
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </BarChart>
              ) : (
                <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 25 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" interval="preserveStartEnd" minTickGap={24} />
                  <YAxis allowDecimals={false} />
                  <Tooltip
                    wrapperStyle={{ pointerEvents: pinnedTooltip ? 'none' : 'auto', outline: 'none' }}
                    content={(props) => {
                      const point = pointFromChartState({
                        activeLabel: props.label,
                        activePayload: props.payload,
                      });
                      if (pinnedTooltip || !props.active || !point) return null;
                      return (
                        <div onMouseEnter={(event) => pinTooltipFromEvent(event, point)}>
                          <FirmwareUpdatesTooltipBody point={point} onSeeDevices={openDeviceList} />
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  {series.map((item, index) => (
                    <Area
                      key={item.key}
                      type="monotone"
                      dataKey={item.key}
                      name={item.label}
                      stackId="updates"
                      stroke={COLORS[index % COLORS.length]}
                      fill={COLORS[index % COLORS.length]}
                      fillOpacity={0.35}
                    />
                  ))}
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="card-footer bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
        <span className="small text-muted">
          {filteredTotal.toLocaleString()} update{filteredTotal === 1 ? '' : 's'} in view
          {metrics ? ` · ${metrics.totals.success.toLocaleString()} succeeded, ${metrics.totals.failure.toLocaleString()} failed` : ''}
          {metrics ? ` · ${GRANULARITY.find((option) => option.value === metrics.bucket)?.label.toLowerCase() || metrics.bucket}` : ''}
          {metrics ? ` · grouped by ${GROUP_OPTIONS.find((option) => option.value === groupBy)?.label.toLowerCase()}` : ''}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={fetchMetrics}
          disabled={loading}
        >
          Refresh
        </button>
      </div>
      {pinnedTooltip && createPortal(
        <div
          style={{
            position: 'fixed',
            left: pinnedTooltip.left,
            top: pinnedTooltip.top,
            zIndex: 1080,
          }}
          onMouseLeave={() => setPinnedTooltip(null)}
        >
          <FirmwareUpdatesTooltipBody
            point={pinnedTooltip.point}
            onSeeDevices={openDeviceList}
          />
        </div>,
        document.body
      )}
      {deviceList && (
        <FirmwareUpdateDevicesModal
          selection={deviceList}
          data={deviceListData}
          loading={deviceListLoading}
          error={deviceListError}
          onClose={closeDeviceList}
        />
      )}
    </div>
  );
};

export default FirmwareUpdatesChart;
